/* eslint-disable no-param-reassign  */
/* eslint-disable no-restricted-syntax  */
import { useCallback, useState } from 'react';

import * as Sentry from '@sentry/react';

const objectTarget = (target, date) => ({
  file: target.files && target.files[0],
  checkbox: target.checked,
  date,
  time: date,
});

const getEventValue = ({ target }, date) => {
  const formatedObject = objectTarget(target, date);

  const { type } = target;

  if (Object.keys(formatedObject).includes(type)) {
    return formatedObject[type];
  }

  return target.value;
};

/* El <Select> de MUI renderiza un input nativo oculto SIN id (el id queda en el
   div visible). Cuando el evento llega por ese input -- autofill del navegador,
   IME/teclado en Android -- target.id resuelve a '' y el formulario se cae.
   Por eso se cae al name antes de mirar currentTarget. */
const resolveId = (event) =>
  [event.target?.id, event.target?.name, event.currentTarget?.id, event.currentTarget?.name].find(
    Boolean,
  );

/* eslint-disable complexity */
function deconstructEvent(event, date) {
  let id;
  let value;
  if (event.type === 'change') {
    // MUI clona eventos nativos, que no traen persist().
    event.persist?.();
    value = getEventValue(event, date);
    id = resolveId(event);
  } else if (event.type === 'click') {
    id = event.target.name;
    value = event.target.value;
  }

  return { id, value };
}

const initialStateForm = (validations, currentErrors = {}) => {
  const initialState = { ...currentErrors };

  Object.keys(validations).forEach((key) => {
    if (!Array.isArray(initialState[key])) {
      initialState[key] = [];
    }
  });

  return initialState;
};

const validateChangeCallback = ({ actions, options }) =>
  actions?.controlsChanged == null && options?.onChange == null;

const callValidate = ({ options, validate, id, value }) => {
  // Either validate on change or clear errors if it is valid
  const { validateOnChange } = options;

  if (validateOnChange) {
    validate(id, value);
  }

  return validateOnChange;
};

const callSetErrors = ({ allowSetErrors, setErrors, id }) => {
  if (allowSetErrors) {
    setErrors((newErrors) => ({ ...newErrors, [id]: [] }));
  }
};

const handleChangeValidation = ({ options, validate, isValid, id, value, setErrors }) => {
  const validateOnChange = callValidate({ options, validate, id, value });

  const isContentValid = isValid(id, value);
  const allowSetErrors = !validateOnChange && isContentValid;

  callSetErrors({ allowSetErrors, setErrors, id });
};

const assignDefault = ({ newErrors, id }) => {
  if (!newErrors[id]) {
    newErrors[id] = [];
  }
};

const validationMessage = (validation) => validation.message ?? 'Campo inválido';

/* Los check() los declara cada formulario y varios asumen que el valor nunca es
   undefined. Si uno revienta, el campo queda inválido -- no se deja al usuario
   en la pantalla de error con el formulario entero caído. */
const runCheck = (validation, value, id) => {
  try {
    return validation.check(value);
  } catch (error) {
    Sentry.captureException(error, { tags: { field: id } });

    return false;
  }
};

const pushNewError = ({ validation, value, isValidValue, newErrors, id }) => {
  let isValid = isValidValue;

  if (!runCheck(validation, value, id)) {
    isValid = false;
    assignDefault({ newErrors, id });
    newErrors.push(validationMessage(validation));
  }

  return { isValid, newErrors };
};

const fillErrors = ({ options, value, id }) => {
  const newErrors = [];
  let isValidValue = true;
  const validations = options.validations?.[id];

  if (!validations) return { newErrors, isValidValue };

  for (const validation of validations) {
    const { isValid } = pushNewError({
      validation,
      value,
      isValidValue,
      newErrors,
      id,
    });

    isValidValue = isValid;
  }

  return { newErrors, isValidValue };
};

const setNewErrors = ({ value, validation, isValid, newErrors, id }) => {
  if (!runCheck(validation, value, id)) {
    isValid = false;

    assignDefault({ newErrors, id });

    newErrors[id].push(validationMessage(validation));
  }

  return { isValid };
};

const fillAllErrors = ({ newErrors, options, value, id }) => {
  let isValid = true;
  const validations = options.validations?.[id];

  // Un campo sin validaciones declaradas no es motivo para tumbar el formulario.
  if (!validations) return { isValid };

  for (const validation of validations) {
    const { isValid: validToReturn } = setNewErrors({
      newErrors,
      validation,
      value,
      id,
      isValid,
    });
    isValid = validToReturn;
  }

  return { isValid };
};

const handleChange = ({ hasControlsChanged, actions, options, id, value, errors, setErrors }) => {
  if (hasControlsChanged) {
    actions.controlsChanged({ [id]: value });
  } else {
    options.onChange(id, value);
  }
  const newErrors = { ...errors };
  // Clear errors because fillAllErrors recreate all
  newErrors[id] = [];

  fillAllErrors({ newErrors, options, value, id });

  setErrors(newErrors);
};

const runOnChangeActions = ({
  id,
  value,
  options = {},
  validate,
  isValid,
  setErrors,
  hasControlsChanged,
  actions,
  errors,
}) => {
  if (id == null) {
    Sentry.captureMessage('Input id is missing', 'warning');

    return;
  }

  handleChangeValidation({ options, validate, isValid, id, value, setErrors });

  handleChange({
    hasControlsChanged,
    actions,
    options,
    id,
    value,
    errors,
    setErrors,
  });
};

const useForm = (controls, actions, options) => {
  const [errors, setErrors] = useState(initialStateForm(options.validations));
  const clearErrors = useCallback(() => {
    setErrors(initialStateForm(options.validations));
  }, [options.validations]);
  if (validateChangeCallback({ actions, options })) {
    throw new Error('Parameters are missing change callback');
  }

  const isValid = useCallback(
    (id, value) =>
      options.validations[id] == null ||
      options.validations[id].every((validation) => runCheck(validation, value, id)),
    [options],
  );

  const validate = useCallback(
    (id, value) => {
      // let isValidValue = true;
      // const newErrors = [];

      // for (const validation of options.validations[id]) {
      //   if (!validation.check(value)) {
      //     isValidValue = false;
      //     if (newErrors[id] == null) {
      //       newErrors[id] = [];
      //     }
      //     newErrors.push(validation.message ?? 'Campo inválido');
      //   }
      // }

      const { newErrors, isValidValue } = fillErrors({ options, value, id });

      setErrors((state) => ({
        ...state,
        [id]: newErrors.length === 0 ? [] : newErrors,
      }));

      return isValidValue;
    },
    [options.validations],
  );

  const validateAll = useCallback(() => {
    const ids = Object.keys(options.validations);
    let isValidForm = true;
    const newErrors = initialStateForm(options.validations);

    ids.forEach((id) => {
      // Los controles se llenan con payloads de API que no siempre traen el campo.
      const value = controls[id] ?? '';
      const { isValid: validField } = fillAllErrors({ newErrors, options, value, id });
      if (isValidForm) {
        isValidForm = validField;
      }
    });

    setErrors(newErrors);

    return isValidForm;
  }, [options.validations, controls]);

  const onChange = useCallback(
    (event) => {
      const hasControlsChanged = actions && 'controlsChanged' in actions;
      const { id, value } = deconstructEvent(event, null); // TODO: Pass date

      /* let id;
      let value;
      if (event.target.type) {
        const eventResult = deconstructEvent(event, null); // TODO: Pass date
        id = eventResult.id;
        value = eventResult.value;
      } else {
        id = event.target.name;
        value = event.target.value;
      } */

      // if (id == null) {
      //   throw new Error('Input is missing id');
      // }
      //
      // // Either validate on change or clear errors if it is valid
      // if (options?.validateOnChange) {
      //   validate(id, value);
      // } else if (isValid(id, value)) {
      //   setErrors((newErrors) => ({ ...newErrors, [id]: [] }));
      // }
      //
      // if (hasControlsChanged) {
      //   actions.controlsChanged({ [id]: value });
      // } else {
      //   options.onChange(id, value);
      // }
      const newErrors = initialStateForm(options.validations, errors);

      runOnChangeActions({
        id,
        value,
        options,
        isValid,
        setErrors,
        hasControlsChanged,
        actions,
        errors: newErrors,
      });
    },
    [options, actions, isValid, validate],
  );

  const onSubmit = useCallback(
    (callback) => {
      const valid = validateAll();

      if (valid) callback();
    },
    [validateAll],
  );

  return { errors, onChange, onSubmit, clearErrors, validate };
};

export default useForm;
